import React from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  CircularProgress, 
  useTheme, 
  alpha, 
  Fade,
  IconButton,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChartActions from './ChartActions';

const ChartContainer = ({ 
  title, 
  children, 
  isLoading, 
  height = '400px', 
  action = null,
  actions = null,
  description = null,
  chartRef = null,
  chartId = null,
  chartType = null,
  sx = {}
}) => {
  const theme = useTheme();
  const isTitleString = typeof title === 'string';
  const showTitleTooltip = isTitleString && title.length > 80;
  
  // Only render chart actions if chartRef is provided and it's meaningful 
  const shouldShowChartActions = chartRef && !isLoading;
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 3, 
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(229, 231, 235, 1)',
        backgroundColor: 'background.paper',
        ...sx
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          pb: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Tooltip
            title={showTitleTooltip ? title : ''}
            arrow
            placement="top"
            disableHoverListener={!showTitleTooltip}
          >
            <Typography 
              variant="h6"
              noWrap={isTitleString}
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '1rem',
                maxWidth: { xs: '70vw', md: '50vw' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
          </Tooltip>
          
          {description && (
            <Tooltip title={description} arrow placement="top">
              <IconButton size="small" sx={{ ml: 0.5, color: theme.palette.text.secondary }}>
                <InfoOutlinedIcon fontSize="small" sx={{ fontSize: '0.85rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {shouldShowChartActions && (
            <ChartActions 
              chartId={chartId || title.replace(/\s+/g, '-').toLowerCase()} 
              title={title} 
              chartRef={chartRef}
              chartType={chartType || 'bar'}
            />
          )}
          
          {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: action ? 1 : 0 }}>
              {actions}
            </Box>
          )}
          
          {action || (
            !shouldShowChartActions && !actions && (
              <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            )
          )}
        </Box>
      </Box>
      
      <Box sx={{ height, position: 'relative' }}>
        <Fade in={!isLoading}>
          <Box sx={{ height: '100%', display: isLoading ? 'none' : 'block' }}>
            {children}
          </Box>
        </Fade>
        
        <Fade in={isLoading}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
              zIndex: 10,
              visibility: isLoading ? 'visible' : 'hidden'
            }}
          >
            <CircularProgress 
              size={36} 
              thickness={4} 
              sx={{ 
                color: theme.palette.primary.main,
              }} 
            />
          </Box>
        </Fade>
      </Box>
    </Paper>
  );
};

export default ChartContainer; 